import { ThemeEvents, VariantUpdateEvent } from '@theme/events';

/**
 * A custom element that displays a product price.
 * This component listens for variant update events and updates the price display accordingly.
 * It handles price updates from two different sources:
 * 1. Variant picker (in quick add modal or product page)
 * 2. Swatches variant picker (in product cards)
 */
class ProductPrice extends HTMLElement {
  connectedCallback() {
    const closestSection = this.closest('.shopify-section, dialog');
    if (!closestSection) return;
    closestSection.addEventListener(ThemeEvents.variantUpdate, this.updatePrice);
    // Ensure multiple price instances (mobile/desktop) update together by also listening globally
    document.addEventListener(ThemeEvents.variantUpdate, this.updatePrice);
  }

  disconnectedCallback() {
    const closestSection = this.closest('.shopify-section, dialog');
    if (!closestSection) return;
    closestSection.removeEventListener(ThemeEvents.variantUpdate, this.updatePrice);
    document.removeEventListener(ThemeEvents.variantUpdate, this.updatePrice);
  }

  /**
   * Updates the price.
   * @param {VariantUpdateEvent} event - The variant update event.
   */
  updatePrice = (event) => {
    // If this event contains a new product (combined listings), sync our product id
    if (event.detail.data.newProduct) {
      this.dataset.productId = event.detail.data.newProduct.id;
    }

    // If the event target has a product id, ensure it matches ours; otherwise allow global updates
    if (
      event.target instanceof HTMLElement &&
      event.target.dataset.productId &&
      event.target.dataset.productId !== this.dataset.productId
    ) {
      return;
    }

    const currentPrice = this.querySelector('[ref="priceContainer"]');
    if (!currentPrice) return;

    // Check if HTML is from a fetched section (not the current document)
    const isFetchedHtml = event.detail.data.html !== document && event.detail.data.html !== window.document;

    // If we have fetched HTML, try to get new price from it
    if (isFetchedHtml) {
      const newPrice = event.detail.data.html.querySelector(
        `product-price[data-product-id="${this.dataset.productId}"] [ref="priceContainer"]`
      );
      
      if (newPrice && currentPrice.innerHTML !== newPrice.innerHTML) {
        currentPrice.replaceWith(newPrice);
      }
      return;
    }

    // Local update: Update price directly from variant data (instant, no fetch)
    if (event.detail.resource) {
      const variant = event.detail.resource;
      this.#updatePriceFromVariant(currentPrice, variant);
    }
  };

  /**
   * Update price container from variant data
   * @param {HTMLElement} priceContainer - The price container element
   * @param {Object} variant - The variant object with price data
   */
  #updatePriceFromVariant(priceContainer, variant) {
    if (!variant.price) return;

    const priceElement = priceContainer.querySelector('.price');
    const comparePriceElement = priceContainer.querySelector('.compare-at-price');
    const showSalePriceFirst = priceContainer.closest('product-price')?.dataset.showSalePriceFirst === 'true';
    
    // Get current price text to determine format
    const currentPriceText = priceElement?.textContent.trim() || '';
    
    // Format prices
    const formattedPrice = this.#formatMoney(variant.price);
    const formattedComparePrice = variant.compare_at_price && variant.compare_at_price > variant.price
      ? this.#formatMoney(variant.compare_at_price)
      : null;
    
    const isOnSale = !!formattedComparePrice;
    
    // Update price element
    if (priceElement && formattedPrice && formattedPrice !== currentPriceText) {
      priceElement.textContent = formattedPrice;
    }
    
    // Handle compare price (sale price)
    if (isOnSale) {
      if (!comparePriceElement) {
        // Create compare price element if it doesn't exist
        const comparePriceSpan = document.createElement('span');
        comparePriceSpan.className = 'compare-at-price';
        comparePriceSpan.setAttribute('role', 'group');
        
        const hiddenSpan = document.createElement('span');
        hiddenSpan.className = 'visually-hidden';
        hiddenSpan.textContent = 'Regular price ';
        comparePriceSpan.appendChild(hiddenSpan);
        
        const priceTextSpan = document.createElement('span');
        priceTextSpan.textContent = formattedComparePrice;
        comparePriceSpan.appendChild(priceTextSpan);
        
        if (showSalePriceFirst && priceElement) {
          priceElement.parentNode.insertBefore(comparePriceSpan, priceElement);
        } else if (priceElement) {
          priceElement.parentNode.insertBefore(comparePriceSpan, priceElement.nextSibling);
        }
      } else {
        // Update existing compare price
        const compareText = comparePriceElement.querySelector('span:not(.visually-hidden)');
        if (compareText && compareText.textContent !== formattedComparePrice) {
          compareText.textContent = formattedComparePrice;
        }
      }
    } else if (comparePriceElement) {
      // Remove compare price if variant is not on sale
      comparePriceElement.remove();
    }
  };

  /**
   * Format money value using Shopify's money format without trailing zeros
   * @param {number} cents - Price in cents
   * @returns {string} Formatted price string
   */
  #formatMoney(cents) {
    if (typeof cents !== 'number' || isNaN(cents)) return '';
    
    // Use Shopify's global money format if available, then remove trailing zeros
    if (typeof window.Shopify !== 'undefined' && window.Shopify.formatMoney) {
      const formatted = window.Shopify.formatMoney(cents);
      // Remove trailing zeros (e.g., "$100.00" -> "$100", "$100.50" -> "$100.50")
      return formatted.replace(/\.00$/, '').replace(/,00$/, '');
    }
    
    // Fallback: Use the existing price element's format as reference
    // This ensures we match the format already used on the page
    const existingPrice = this.querySelector('.price');
    if (existingPrice && existingPrice.textContent) {
      // Extract format from existing price (e.g., "$100" or "$100.00")
      const existingText = existingPrice.textContent.trim();
      const existingMatch = existingText.match(/^([^\d]*)(\d+(?:[.,]\d+)?)/);
      if (existingMatch) {
        const currencySymbol = existingMatch[1].trim();
        const dollars = cents / 100;
        // Format without trailing zeros
        const formatted = dollars.toLocaleString('en-US', {
          minimumFractionDigits: 0,
          maximumFractionDigits: 2
        }).replace(/\.00$/, '');
        return currencySymbol ? `${currencySymbol}${formatted}` : `$${formatted}`;
      }
    }
    
    // Final fallback: Basic formatting without trailing zeros
    const dollars = cents / 100;
    return dollars.toLocaleString('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 2
    }).replace(/\.00$/, '');
  }
}

if (!customElements.get('product-price')) {
  customElements.define('product-price', ProductPrice);
}
